// Artefact shop: pure data/decision logic plus the HTML templates for the shop cards and ink counter — no
// DOM APIs anywhere in this file (same split as teamProfile.ts/radarChart.ts). hud.ts owns mounting, event
// wiring and the actual game.apply() calls; this only decides what buying/selling costs and whether it's
// currently allowed, plus renders that into markup.
//
// docs/game-design.md, "Артефакты" (rewritten 2026-09-23): the three-tier system is gone. One artefact is
// one flat level at one flat price (core's own `SHOP.price`, 150 ink) — sixteen of them, nine passive (work on
// their own, no button) and seven active (E/R, pressed). Names/kinds/descriptions are read straight off
// core's own `ARTIFACTS` table, not copied here — the shop and the skill bar must never show a different
// story about what a line does.
import { ARTIFACTS, SHOP } from '../core/artifacts.js';
import { RELICS, RELIC_LINES, RELIC_SHOP } from '../core/relics.js';
import type { ArtifactLine, ArtifactSlotKey, RelicLine } from '../core/commands.js';
import type { Team } from '../core/layout.js';
import type { ViewHero, YouStats } from './gameView.js';
import { t, type Params } from './i18n/index.js';
import { artifactName, relicName as relicDisplayName } from './i18n/names.js';

export type { YouStats };

export const ARTIFACT_LINES = Object.keys(ARTIFACTS) as ArtifactLine[];

/** An artefact's or relic's engraved icon, shipped under public/art/artifacts/ (one 256×256 jpg per id). */
export const iconSrc = (id: ArtifactLine | RelicLine) => `art/artifacts/${id}.jpg`;
/** A fixed-size lazy <img> for `id`: the box is reserved before the file arrives, so nothing shifts. */
export const iconImg = (id: ArtifactLine | RelicLine, cls: string, size: number) =>
  `<img class="${cls}" src="${iconSrc(id)}" alt="" width="${size}" height="${size}" loading="lazy" decoding="async">`;

export function lineName(line: ArtifactLine): string { return artifactName(line); }
export function lineKind(line: ArtifactLine): 'passive' | 'active' { return ARTIFACTS[line].kind; }

const pct = (share: number) => Math.round(share * 100);
/** A Склянка charge (share of max hp) as the whole percent shown on the slot and tooltip — floored, so it
    never reads higher than what a sip would heal; the epsilon absorbs cooldown float drift (28 s → 28%). */
export const vialPct = (charge: number) => Math.floor(charge * 100 + 1e-6);
/** Склянка крови's charge per second of recharge, in % of max hp (before Песочные часы). */
export const VIAL_RATE_PCT = Math.round(ARTIFACTS.bloodvial.heal / ARTIFACTS.bloodvial.cd * 1000) / 10;
/** The numbers each artefact's one-line effect (i18n art.<line>.desc) is built from — all read off core's own
    `ARTIFACTS` table, so a retune can never leave the card behind. */
const A = ARTIFACTS;
const totem = (x: typeof A.totemWard | typeof A.totemWrath) => ({ life: x.life, r: x.r, pct: pct(x.pct), hp: x.hp });
const DESC_PARAMS: Record<ArtifactLine, Params> = {
  shoulder: { hp: A.shoulder.hp },
  whetstone: { atk: pct(A.whetstone.atkSpeed) },
  boots: { move: pct(A.boots.moveSpeed), quiet: A.boots.quiet },
  salve: { regen: pct(A.salve.regenPct), quiet: A.salve.quiet },
  hourglass: { rate: pct(A.hourglass.cdRate) },
  mistAmulet: { chance: pct(A.mistAmulet.missChance) },
  graveCandle: { cut: pct(A.graveCandle.respawnCut) },
  dragonTooth: { dmg: pct(A.dragonTooth.dmg), above: pct(A.dragonTooth.above) },
  banner: { cap: pct(A.banner.capture) },
  bloodvial: { rate: VIAL_RATE_PCT, max: pct(A.bloodvial.heal) },
  windoil: { haste: pct(A.windoil.haste), dur: A.windoil.dur },
  cloak: { dur: A.cloak.dur },
  totemWard: totem(A.totemWard),
  totemWrath: totem(A.totemWrath),
  brand: { range: A.brand.range, pct: pct(A.brand.pct), dur: A.brand.dur },
  tar: { range: A.tar.range, r: A.tar.r, life: A.tar.life, slow: pct(A.tar.slow) },
};
export function lineDesc(line: ArtifactLine): string { return t(`art.${line}.desc` as const, DESC_PARAMS[line]); }

export const ARTIFACT_COST: number = SHOP.price;
export const SELL_REFUND: number = Math.floor(SHOP.price * SHOP.sellRefundPct);

/** Ink milestones worth calling out on the counter: enough for a first artefact, enough for both slots —
    the two thresholds a flat price creates, replacing the old three cumulative tier totals. */
export const INK_MILESTONES: readonly number[] = [ARTIFACT_COST, ARTIFACT_COST * 2];

export type HeldArtifacts = { E: ArtifactLine | null; R: ArtifactLine | null };
export function heldOf(artifacts: ViewHero['artifacts']): HeldArtifacts {
  return { E: artifacts.E?.line ?? null, R: artifacts.R?.line ?? null };
}

/** Real seconds left on a cooldown: with Песочные часы held it runs `1 + cdRate` times faster than the clock,
    so the number shown must be divided by that rate, not read as seconds. The fill bar (a fraction) needs no fix. */
export function cdSecondsLeft(cd: number, held: HeldArtifacts): number {
  return held.E === 'hourglass' || held.R === 'hourglass' ? cd / (1 + ARTIFACTS.hourglass.cdRate) : cd;
}

/** Why a card's button is greyed out, as a code: `disabledReason` words it (the title's full sentence),
    actionLabel() picks the card's short text by it. */
export type BuyBlock = 'notInBase' | 'noSlot' | 'locked' | 'noInk';
export type ArtifactAction =
  | { kind: 'buy'; block: BuyBlock | null; disabledReason: string | null; lockedFor?: number }
  | { kind: 'sell'; refund: number; block: BuyBlock | null; disabledReason: string | null };

/** What clicking this card's one button would do right now, and — if it's greyed out — why, in the
    player's own words. Mirrors core's own buyArtifact/sellArtifact gates (artifacts.ts) exactly, checking
    the structural blockers (base, a free slot, the team lock) before the resource one (ink), so a disabled
    button here is never a surprise once the server answers. Never sends anything itself — hud.ts does
    that with game.apply(); this only decides what the player should be told before they click. */
export function artifactAction(params: {
  owned: boolean;
  ink: number;
  inBase: boolean;
  /** Seconds left on a TEAMMATE's lock for this artefact — 0 if not locked for this player. Meaningless
      once owned: core never re-checks the lock on a hero's own already-held artefact. */
  lockedRemaining: number;
  /** Whether at least one of the player's own E/R slots is empty — meaningless once owned. */
  hasEmptySlot: boolean;
}): ArtifactAction {
  const { owned, ink, inBase, lockedRemaining, hasEmptySlot } = params;
  if (owned) return { kind: 'sell', refund: SELL_REFUND, block: inBase ? null : 'notInBase', disabledReason: inBase ? null : t('shop.notInBase') };
  if (!inBase) return { kind: 'buy', block: 'notInBase', disabledReason: t('shop.notInBase') };
  if (!hasEmptySlot) return { kind: 'buy', block: 'noSlot', disabledReason: t('shop.noSlot') };
  if (lockedRemaining > 0) {
    const s = Math.ceil(lockedRemaining);
    return { kind: 'buy', block: 'locked', disabledReason: t('shop.locked', { s }), lockedFor: s };
  }
  if (ink < ARTIFACT_COST) return { kind: 'buy', block: 'noInk', disabledReason: t('shop.noInk', { cost: ARTIFACT_COST }) };
  return { kind: 'buy', block: null, disabledReason: null };
}

/** The card's own button text — the disabled reason is shortened here for the compact card (the full
    sentence still rides in `title`). Pure and tested on its own. */
export function actionLabel(action: ArtifactAction): string {
  if (action.kind === 'sell') return t('shop.sell', { n: action.refund });
  switch (action.block) {
    case null: return t('shop.buy', { cost: ARTIFACT_COST });
    case 'locked': return t('shop.lockedShort', { s: action.lockedFor ?? 0 });
    case 'noInk': return t('shop.needInk', { cost: ARTIFACT_COST });  // the price is the whole reason — hiding it leaves nothing to aim at
    case 'noSlot': return t('shop.noSlotShort');
    case 'notInBase': return action.disabledReason!;  // unreachable in practice, the panel isn't rendered outside base
  }
}

export type ShopItemVM = { line: ArtifactLine; ownedSlot: ArtifactSlotKey | null; action: ArtifactAction };

/** One player's full 10-artefact shop state, derived from their own E/R loadout and their team's active
    locks — the pure model the card template renders and hud.ts's click handlers read back from by line. */
export function buildShopItems(held: HeldArtifacts, you: YouStats): ShopItemVM[] {
  const hasEmptySlot = !held.E || !held.R;
  return ARTIFACT_LINES.map((line): ShopItemVM => {
    const ownedSlot: ArtifactSlotKey | null = held.E === line ? 'E' : held.R === line ? 'R' : null;
    const lockedRemaining = ownedSlot ? 0 : (you.artifactLocks.find(l => l.line === line)?.remaining ?? 0);
    const action = artifactAction({ owned: !!ownedSlot, ink: you.ink, inBase: you.inBase, lockedRemaining, hasEmptySlot });
    return { line, ownedSlot, action };
  });
}

/** A one-line summary of what the player currently holds — "which two you hold" at a glance, before
    reading any individual card. An empty slot reads as an em dash, not a blank. */
export function heldSummary(items: ShopItemVM[]): string {
  const e = items.find(i => i.ownedSlot === 'E')?.line, r = items.find(i => i.ownedSlot === 'R')?.line;
  return `E: ${e ? lineName(e) : '—'} · R: ${r ? lineName(r) : '—'}`;
}

/* ---------------- templates (pure strings — hud.ts sets innerHTML and wires clicks) ---------------- */

/** Card icon edge, px — sits beside the name, so it sets the card's first two rows, not an extra one. */
const SHOP_ICON = 26;

function esc(s: string): string { return s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!)); }

/** One card, fully inline — with a single flat price there is no tier text to hide behind a hover/detail
    dock any more (that machinery existed only to hide the old three-tier breakdown), so "what does it do"
    (name + one-line effect) and "do I press it" (which group it's in + the one action) both fit in the
    card a player already sees, exactly what a glance mid-fight needs. */
export function itemCardHtml(vm: ShopItemVM): string {
  const a = vm.action;
  const btn = `<button type="button" class="shop-card-btn${a.kind === 'sell' ? ' shop-card-btn-sell' : ''}" data-shop-${a.kind}="${vm.line}" ${a.disabledReason ? 'disabled' : ''} title="${a.disabledReason ? esc(a.disabledReason) : ''}">${esc(actionLabel(a))}</button>`;
  return `<div class="shop-card${vm.ownedSlot ? ' owned' : ''}" data-line="${vm.line}" title="${esc(`${lineName(vm.line)} — ${lineDesc(vm.line)}`)}">
    ${iconImg(vm.line, 'shop-card-ico', SHOP_ICON)}<div class="shop-card-top"><b>${esc(lineName(vm.line))}</b>${vm.ownedSlot ? `<span class="shop-slot">${vm.ownedSlot}</span>` : ''}</div>
    <p class="shop-card-desc">${esc(lineDesc(vm.line))}</p>
    ${btn}
  </div>`;
}

/* ---------------- relics (core's relics.ts): one per team, bought for both, never sold ---------------- */

export const RELIC_COST: number = RELIC_SHOP.price;
export function relicName(line: RelicLine): string { return relicDisplayName(line); }
/** Built from core's own numbers, like the totems' text. */
export function relicDesc(line: RelicLine): string {
  switch (line) {
    case 'martyrShield': { const c = RELICS.martyrShield; return t('relic.martyrShield.desc', { dur: c.dur, r: c.r, cd: c.cd }); }
    case 'wildHorn': return t('relic.wildHorn.desc', { move: pct(RELICS.wildHorn.moveSpeed), atk: pct(RELICS.wildHorn.atkSpeed) });
    case 'bestiaryEye': { const c = RELICS.bestiaryEye; return t('relic.bestiaryEye.desc', { dur: c.dur, dmg: pct(c.dmgBonus), rate: pct(c.cdRate), cd: c.cd }); }
    case 'hydraHeart': return t('relic.hydraHeart.desc', { regen: pct(RELICS.hydraHeart.regenPct) });
  }
}

/** Who holds this relic, from the viewer's side: 'ours', 'theirs', or 'free' (in the shop). */
export type RelicState = 'ours' | 'theirs' | 'free';
export type RelicBlock = 'ours' | 'theirs' | 'teamHasOne' | 'notInBase' | 'noInk';
export type RelicItemVM = { line: RelicLine; state: RelicState; block: RelicBlock | null; disabledReason: string | null };

/** Mirrors core's buyRelic gates: in the shop (not held by anyone), the team holding none, at base, the ink. */
export function buildRelicItems(relics: Readonly<Record<Team, RelicLine | null>>, team: Team, you: Pick<YouStats, 'ink' | 'inBase'>): RelicItemVM[] {
  const rival: Team = team === 'a' ? 'b' : 'a';
  return RELIC_LINES.map((line): RelicItemVM => {
    const state: RelicState = relics[team] === line ? 'ours' : relics[rival] === line ? 'theirs' : 'free';
    const block: RelicBlock | null = state === 'ours' ? 'ours' : state === 'theirs' ? 'theirs' : relics[team] ? 'teamHasOne'
      : !you.inBase ? 'notInBase' : you.ink < RELIC_COST ? 'noInk' : null;
    const disabledReason = block === null ? null
      : block === 'notInBase' ? t('shop.notInBase') : block === 'noInk' ? t('shop.noInk', { cost: RELIC_COST }) : t(`shop.relic.${block}` as const);
    return { line, state, block, disabledReason };
  });
}

export function relicActionLabel(vm: RelicItemVM): string {
  if (vm.state === 'ours') return t('shop.relic.oursShort');
  if (vm.state === 'theirs') return t('shop.relic.theirsShort');
  if (vm.block === null) return t('shop.buy', { cost: RELIC_COST });
  if (vm.block === 'teamHasOne') return t('shop.relic.teamHasOneShort');
  if (vm.block === 'noInk') return t('shop.needInk', { cost: RELIC_COST });
  return vm.disabledReason!;
}

export function relicCardHtml(vm: RelicItemVM): string {
  const r = vm.disabledReason;
  const btn = `<button type="button" class="shop-card-btn" data-relic-buy="${vm.line}" ${r ? 'disabled' : ''} title="${r ? esc(r) : ''}">${esc(relicActionLabel(vm))}</button>`;
  return `<div class="shop-card relic ${vm.state}" data-relic="${vm.line}" title="${esc(`${relicName(vm.line)} — ${relicDesc(vm.line)}`)}">
    ${iconImg(vm.line, 'shop-card-ico', SHOP_ICON)}<div class="shop-card-top"><b>${esc(relicName(vm.line))}</b><span class="shop-slot">${RELICS[vm.line].kind === 'active' ? 'T' : t('shop.relic.auto')}</span></div>
    <p class="shop-card-desc">${esc(relicDesc(vm.line))}</p>
    ${btn}
  </div>`;
}

/** The «Реликвии» section: its own rules in one line, then the cards. */
export function relicSectionHtml(items: RelicItemVM[]): string {
  return `<div class="shop-group-label">${t('shop.relicsLabel', { cost: RELIC_COST })}</div>
    <div class="shop-grid shop-grid-relic">${items.map(relicCardHtml).join('')}</div>`;
}

/** Two labelled groups, not one mixed grid: a passive's whole story is "it's already working for you", an
    active's is "you press this" — grouping by kind answers that question before a single card is read,
    the same order the brief asks for ("what does it do", then "do I press it"). */
export function shopBodyHtml(items: ShopItemVM[], relics: RelicItemVM[] = []): string {
  const passive = items.filter(i => lineKind(i.line) === 'passive'), active = items.filter(i => lineKind(i.line) === 'active');
  return `<div class="shop-head-row"><div class="shop-group-label">${t('shop.passiveLabel')}</div><div class="shop-held">${esc(heldSummary(items))}</div></div>
    <div class="shop-grid shop-grid-passive">${passive.map(itemCardHtml).join('')}</div>
    <div class="shop-group-label">${t('shop.activeLabel')}</div>
    <div class="shop-grid shop-grid-active">${active.map(itemCardHtml).join('')}</div>${relics.length ? relicSectionHtml(relics) : ''}`;
}
